import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { UserPlus, Users2 } from 'lucide-react'
import {
  Bidi, Card, CardHeader, EmptyState, ErrorState, Identifier, LoadingState,
  NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, assignFloorToTeam, clearFloorTeam, clearSubstitution, createTeam,
  disbandTeam, fetchTeams, formatCount, may, nameIn, substituteMember,
  translateError,
  type Team, type TeamsView,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/* ============================================================================
   W-42 · TEAMS. Who works with whom, and on which floor.

   ── THE TWO SENTENCES THIS SCREEN IS BUILT FROM ──────────────────────────────
   *"there is a team of 2 who does the cleaning, each floor has 1 team"* and
   *"supervisor will substitute"* — the client, 16 September.

   ── WHY IT IS NOT PART OF THE ASSIGNMENT BOARD ───────────────────────────────
   The board is where an EXCEPTION is fixed: one room, one man, at half past nine. This
   is where the morning is SET UP: sixteen decisions, made once, before anybody is on a
   floor. Putting both on one screen would mean a supervisor hunting for the small
   control among the large ones at exactly the time of day he has least patience.

   ── THREE SECTIONS, IN THE ORDER THE MORNING HAPPENS ─────────────────────────
   Floors first, because that is the decision: every floor either has a pair or does not,
   and the ones that do not are the whole job. Then the pairs themselves, where covering
   for an absent man lives. Then whoever is on no team — which is both the list a new pair
   is made from and the answer to "why is floor four short today".

   ── AND A TEAM HAS NO NAME ───────────────────────────────────────────────────
   Every label on this screen is two men's names joined by a plus. There is no name to
   fall back on, deliberately: a supervisor radios a person, not a team number.
   ========================================================================= */

const nameOf = (locale: 'en' | 'ar', team: Team) =>
  `${nameIn(locale, team.memberOneName, team.memberOneNameAr)} + ${
    nameIn(locale, team.memberTwoName, team.memberTwoNameAr)}`

export function Teams({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const client = useQueryClient()

  const [pairOne, setPairOne] = useState('')
  const [pairTwo, setPairTwo] = useState('')
  const [moved, setMoved] = useState<number | null>(null)

  const teams = useQuery({
    queryKey: ['teams', propertyId],
    queryFn: ({ signal }) => fetchTeams(propertyId, { signal }),
  })

  /* EVERY MUTATION REFETCHES THE WHOLE VIEW RATHER THAN PATCHING IT.
     Giving a floor to a pair changes that pair's load, the other pair's load if it had
     the floor, the floor's own row and the rooms behind all three. A client that updated
     only what it sent would draw a screen that disagrees with the one it would get if it
     asked — the rule this project settled on in Sprint 34. */
  const refresh = () => client.invalidateQueries({ queryKey: ['teams', propertyId] })

  const pair = useMutation({
    mutationFn: () => createTeam(propertyId, pairOne, pairTwo),
    onSuccess: () => { setPairOne(''); setPairTwo(''); void refresh() },
  })

  const disband = useMutation({
    mutationFn: (teamId: string) => disbandTeam(propertyId, teamId),
    onSuccess: () => void refresh(),
  })

  const cover = useMutation({
    mutationFn: (v: { teamId: string; absent: string; standIn: string }) =>
      substituteMember(propertyId, v.teamId, v.absent, v.standIn),
    onSuccess: () => void refresh(),
  })

  const uncover = useMutation({
    mutationFn: (teamId: string) => clearSubstitution(propertyId, teamId),
    onSuccess: () => void refresh(),
  })

  const giveFloor = useMutation({
    mutationFn: (v: { floorId: string; teamId: string }) =>
      v.teamId === ''
        ? clearFloorTeam(propertyId, v.floorId).then(() => ({ roomsMoved: 0 }))
        : assignFloorToTeam(propertyId, v.floorId, v.teamId),
    onSuccess: (result) => { setMoved(result.roomsMoved); void refresh() },
  })

  const failure = pair.error ?? cover.error ?? giveFloor.error ?? disband.error

  if (!may('task.assign')) return <NoPermissionState />
  if (teams.isLoading) return <LoadingState />
  if (teams.error && !teams.data) {
    if (teams.error instanceof ApiError && teams.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(teams.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={teams.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  const view = teams.data as TeamsView

  return (
    /* NO SCREEN-LEVEL HEADING HERE. The shell already draws "Teams — who works with whom,
       and on which floor" above this, and the first render of this screen said it twice.
       **A heading names what is UNDER it; the shell owns the screen's name** — the rule
       this project has written down five times, and the sixth was found by taking a
       picture of the screen and looking at it. */
    <div className="flex flex-col gap-4">
      {failure && (
        <div role="alert" className="rounded-hk border border-destructive/40 bg-destructive/5 px-3 py-2 text-[13px]">
          <Bidi>{translateError(failure, locale).message}</Bidi>
        </div>
      )}

      {moved !== null && (
        /* WHAT IT DID, IN THE TERMS THE QUESTION WAS ASKED IN. "Done" does not answer
           "is the board now right"; a number of rooms does. `role="status"` rather than
           `alert`: it is the result of something the supervisor just pressed, not an
           interruption. */
        <div role="status" className="rounded-hk border border-border bg-muted px-3 py-2 text-[13px]">
          {t.teamsRoomsMoved.replace('{count}', formatCount(moved, locale))}
        </div>
      )}

      {/* ── FLOORS ────────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader title={t.teamsFloors} />
        <div className="flex flex-col divide-y divide-border">
          {view.floors.map((f) => (
            <div key={f.floorId} className="flex flex-wrap items-center gap-3 py-2.5">
              <div className="flex min-w-48 flex-col">
                <span className="text-[13px] font-medium">
                  {nameIn(locale, f.buildingName, f.buildingNameAr)}
                  {' · '}
                  {t.editFloorCode} <Identifier>{f.floorCode}</Identifier>
                </span>
                <span className="text-[12px] text-muted-foreground">
                  {formatCount(f.rooms, locale)} {t.rooms}
                </span>
              </div>

              <label className="flex items-center gap-2 text-[12px]">
                <span className="text-muted-foreground">{t.teamsGiveFloor}</span>
                <select
                  id={`floor-team-${f.floorId}`}
                  aria-label={`${t.teamsGiveFloor} ${f.floorCode}`}
                  value={f.teamId ?? ''}
                  disabled={giveFloor.isPending}
                  onChange={(e) =>
                    giveFloor.mutate({ floorId: f.floorId, teamId: e.target.value })}
                  className="h-8 rounded-hk border border-input bg-background px-2 text-[13px]"
                >
                  <option value="">{t.teamsNobody}</option>
                  {view.teams.map((team) => (
                    <option key={team.teamId} value={team.teamId}>
                      {nameOf(locale, team)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          ))}
        </div>
      </Card>

      {/* ── PAIRS ─────────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader title={t.teamsPairs} />
        {view.teams.length === 0 ? (
          <EmptyState title={t.teamsNoPairs} body={t.teamsNoPairsBody} />
        ) : (
          <div className="flex flex-col divide-y divide-border">
            {view.teams.map((team) => (
              <div key={team.teamId} className="flex flex-wrap items-center gap-3 py-2.5">
                <Users2 className="size-4 text-muted-foreground" aria-hidden />
                <div className="flex min-w-56 flex-col">
                  <span className="text-[13px] font-medium"><Bidi>{nameOf(locale, team)}</Bidi></span>
                  <span className="text-[12px] text-muted-foreground">
                    {team.floors.length === 0
                      ? t.teamsNoFloor
                      : team.floors
                          .map((f) => `${nameIn(locale, f.buildingName, f.buildingNameAr)} · ${f.floorCode}`)
                          .join(' · ')}
                    {' — '}
                    {t.teamsHolding.replace('{count}', formatCount(team.openRooms, locale))}
                  </span>
                </div>

                {team.standInName ? (
                  <div className="flex items-center gap-2 text-[12px]">
                    <span>
                      <Bidi>{nameIn(locale, team.standInName, team.standInNameAr)}</Bidi>
                      {' '}{t.teamsCovering}{' '}
                      <Bidi>
                        {team.absentUserId === team.memberOneUserId
                          ? nameIn(locale, team.memberOneName, team.memberOneNameAr)
                          : nameIn(locale, team.memberTwoName, team.memberTwoNameAr)}
                      </Bidi>
                    </span>
                    <button
                      type="button"
                      onClick={() => uncover.mutate(team.teamId)}
                      className="rounded-hk border border-border px-2 py-1 hover:bg-muted cursor-pointer"
                    >
                      {t.teamsClearCover}
                    </button>
                  </div>
                ) : (
                  <CoverControl
                    team={team}
                    view={view}
                    locale={locale}
                    t={t}
                    busy={cover.isPending}
                    onCover={(absent, standIn) =>
                      cover.mutate({ teamId: team.teamId, absent, standIn })}
                  />
                )}

                <button
                  type="button"
                  onClick={() => disband.mutate(team.teamId)}
                  className="ms-auto rounded-hk border border-border px-2 py-1 text-[12px]
                             hover:bg-muted cursor-pointer"
                >
                  {t.teamsDisband}
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── WHOEVER IS ON NO TEAM ─────────────────────────────────────────── */}
      <Card>
        <CardHeader title={t.teamsUnpaired} />
        <div className="flex flex-wrap items-end gap-3">
          {(['one', 'two'] as const).map((slot) => (
            <label key={slot} className="flex flex-col gap-1 text-[12px]">
              <span className="text-muted-foreground">{t.teamsChoose}</span>
              <select
                id={`pair-${slot}`}
                aria-label={`${t.teamsMakePair} ${slot === 'one' ? 1 : 2}`}
                value={slot === 'one' ? pairOne : pairTwo}
                onChange={(e) =>
                  slot === 'one' ? setPairOne(e.target.value) : setPairTwo(e.target.value)}
                className="h-8 min-w-44 rounded-hk border border-input bg-background px-2 text-[13px]"
              >
                <option value="">—</option>
                {view.unpaired.map((u) => (
                  /* A MAN WHO IS NOT IN TODAY IS DRAWN AND NOT CHOOSABLE. Hiding him
                     would leave a supervisor looking for somebody who is simply on
                     leave, which is the question the screen should be answering. */
                  <option key={u.userId} value={u.userId} disabled={!u.onShift}>
                    {nameIn(locale, u.name, u.nameAr)}
                    {u.onShift ? '' : ` — ${t.teamsOffToday}`}
                  </option>
                ))}
              </select>
            </label>
          ))}

          <button
            type="button"
            disabled={pairOne === '' || pairTwo === '' || pairOne === pairTwo || pair.isPending}
            onClick={() => pair.mutate()}
            className="inline-flex h-8 items-center gap-1.5 rounded-hk border border-border px-3
                       text-[12px] font-medium hover:bg-muted cursor-pointer
                       disabled:cursor-not-allowed disabled:opacity-50"
          >
            <UserPlus className="size-3.5" aria-hidden />
            {t.teamsPairThem}
          </button>
        </div>
      </Card>
    </div>
  )
}

/** Covering for one of the two, for today. The stand-in list is whoever is on no pair
 *  and is actually in — a man already on another floor's pair would be covering two. */
function CoverControl({
  team, view, locale, t, busy, onCover,
}: {
  team: Team
  view: TeamsView
  locale: 'en' | 'ar'
  t: typeof STRINGS['en']
  busy: boolean
  onCover: (absentUserId: string, standInUserId: string) => void
}) {
  const [absent, setAbsent] = useState('')
  const available = useMemo(() => view.unpaired.filter((u) => u.onShift), [view.unpaired])

  return (
    <div className="flex items-center gap-2 text-[12px]">
      <select
        id={`absent-${team.teamId}`}
        aria-label={`${t.teamsCoverFor} — ${team.memberOneName} + ${team.memberTwoName}`}
        value={absent}
        onChange={(e) => setAbsent(e.target.value)}
        className="h-8 rounded-hk border border-input bg-background px-2 text-[13px]"
      >
        <option value="">{t.teamsCoverFor}</option>
        <option value={team.memberOneUserId}>
          {nameIn(locale, team.memberOneName, team.memberOneNameAr)}
        </option>
        <option value={team.memberTwoUserId}>
          {nameIn(locale, team.memberTwoName, team.memberTwoNameAr)}
        </option>
      </select>

      <select
        id={`standin-${team.teamId}`}
        aria-label={`${t.teamsCover} — ${team.memberOneName} + ${team.memberTwoName}`}
        disabled={absent === '' || busy || available.length === 0}
        value=""
        onChange={(e) => e.target.value !== '' && onCover(absent, e.target.value)}
        className="h-8 rounded-hk border border-input bg-background px-2 text-[13px]
                   disabled:cursor-not-allowed disabled:opacity-50"
      >
        <option value="">{t.teamsCover}</option>
        {available.map((u) => (
          <option key={u.userId} value={u.userId}>{nameIn(locale, u.name, u.nameAr)}</option>
        ))}
      </select>
    </div>
  )
}
